import React, { useState, useEffect } from "react";
import { db, ref, onValue, update, remove, get, set } from "./firebase";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "./assets/lms-logo.png";
import logoPath from "./assets/Xmaths.png";
import { formatCourseId } from "./utils/formatCourse";

const AdminExamGrades = () => {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchById, setSearchById] = useState("");
  const [newExam, setNewExam] = useState({
    studentId: "",
    examName: "",
    grade: "",
    mark: "",
    comment: ""
  });
  const [editableGrades, setEditableGrades] = useState({});
  const [importExamName, setImportExamName] = useState("");
  const [excelData, setExcelData] = useState(null);
  const [isAddGradeCollapsed, setIsAddGradeCollapsed] = useState(true);
  const [isImportExportCollapsed, setIsImportExportCollapsed] = useState(true);
  const [isCreateExamCollapsed, setIsCreateExamCollapsed] = useState(true);
  const [availableExams, setAvailableExams] = useState([]);
  const [examDetails, setExamDetails] = useState({});
  const [newExamDetails, setNewExamDetails] = useState({
    name: "",
    totalMark: "",
    date: ""
  });
  const [activeSection, setActiveSection] = useState(null); // 'create', 'add', 'import', or null

  const gradeOptions = ["A*", "A", "B", "C", "D", "U"];

  useEffect(() => {
    const coursesRef = ref(db, "courses");
    onValue(coursesRef, (snapshot) => {
      if (snapshot.exists()) {
        setCourses(Object.keys(snapshot.val()));
      }
    });
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      // Fetch students
      const studentsRef = ref(db, `courses/${selectedCourse}/students`);
      onValue(studentsRef, (snapshot) => {
        if (snapshot.exists()) {
          const studentsData = Object.entries(snapshot.val()).map(([studentId, student]) => ({
            studentId,
            ...student,
            examGrades: student.examGrades || {}
          }));
          setStudents(studentsData);
        } else {
          setStudents([]);
        }
      });

      // Fetch exams
      const examsRef = ref(db, `courses/${selectedCourse}/exams`);
      onValue(examsRef, (snapshot) => {
        if (snapshot.exists()) {
          const examsData = snapshot.val();
          setAvailableExams(Object.keys(examsData));
          setExamDetails(examsData);
        } else {
          setAvailableExams([]);
          setExamDetails({});
        }
      });
    }
  }, [selectedCourse]);

  const getStudentsInCourse = () => {
    if (!selectedCourse) return [];

    return students
      .map(student => ({
        studentId: student.studentId,
        userName: student.name,
        exam: selectedExam ? student.examGrades[selectedExam] || null : null
      }))
      .filter(student => {
        const matchesSearch = !searchQuery ||
          student.userName.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesId = !searchById ||
          student.studentId.toString().includes(searchById);
        return matchesSearch && matchesId;
      })
      .sort((a, b) => (a.userName || '').localeCompare(b.userName || '')); // Sort alphabetically by name
  };

  const filteredStudents = getStudentsInCourse();

  const addExam = async () => {
    const { studentId, examName, grade, mark, comment } = newExam;

    if (!studentId || !examName) {
      alert("Please fill in all required fields.");
      return;
    }

    const student = students.find((s) => s.studentId === studentId);
    if (!student) {
      alert("Student ID not found.");
      return;
    }

    // Check if exam exists in course
    if (!examDetails[examName]) {
      alert("Exam not found in course.");
      return;
    }

    const path = `courses/${selectedCourse}/students/${studentId}/examGrades/${examName}`;
    const examData = {
      ...(grade && { grade }),
      ...(mark && { mark: parseFloat(mark) }),
      totalMark: examDetails[examName].totalMark,
      ...(comment && { comment })
    };

    try {
      await update(ref(db, path), examData);
      alert("Student grade added successfully!");
      setNewExam({
        studentId: "",
        examName: "",
        grade: "",
        mark: "",
        comment: ""
      });
      setIsAddGradeCollapsed(true);
    } catch (error) {
      console.error("Error adding student grade:", error);
      alert("Failed to add student grade. Please check the console for details.");
    }
  };

  const deleteExam = async (studentId, examName) => {
    if (!window.confirm("Are you sure you want to delete this exam?")) return;

    try {
      await remove(ref(db, `courses/${selectedCourse}/students/${studentId}/examGrades/${examName}`));
      alert("Exam deleted successfully!");
    } catch (error) {
      console.error("Error deleting exam:", error);
      alert("Failed to delete exam. Please check the console for details.");
    }
  };

  const handleExcelImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      setExcelData(XLSX.utils.sheet_to_json(firstSheet));
    };
    reader.readAsArrayBuffer(file);
  };

  const processExcelImport = () => {
    if (!importExamName) {
      alert("Please select an exam for the imported grades.");
      return;
    }

    if (!excelData) {
      alert("Please upload an Excel file.");
      return;
    }

    const updates = {};
    excelData.forEach((row) => {
      if (row["Student ID"]) {
        const student = students.find(s => s.studentId === row["Student ID"].toString());
        if (student) {
          const path = `courses/${selectedCourse}/students/${student.studentId}/examGrades/${importExamName}`;
          updates[path] = {
            ...(row["Grade"] && { grade: row["Grade"] }),
            ...(row["Mark"] && { mark: parseFloat(row["Mark"]) }),
            totalMark: examDetails[importExamName].totalMark,
            ...(row["Comment"] && { comment: row["Comment"] })
          };
        }
      }
    });

    update(ref(db), updates)
      .then(() => {
        alert("Excel imported successfully!");
        setExcelData(null);
        setImportExamName("");
      })
      .catch((error) => {
        console.error("Error importing Excel:", error);
        alert("Failed to import Excel. Please check the console for details.");
      });
  };

  const handleExcelExport = () => {
    if (!selectedExam) return;

    const exportData = filteredStudents.map(student => ({
      "Student ID": student.studentId,
      "Name": student.userName,
      "Grade": student.exam?.grade || "",
      "Mark": student.exam?.mark || "",
      "Total Mark": student.exam?.totalMark || "",
      "Comment": student.exam?.comment || ""
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Grades");

    const instructions = [
      ["GRADE IMPORT INSTRUCTIONS"],
      [],
      ["Column", "Required", "Format", "Notes"],
      ["Student ID", "Yes", "Text/Number", "Must match existing IDs"],
      ["Grade", "No", "A*, A, B, etc.", "Empty = Did Not Attend"],
      ["Mark", "No", "Number", "Empty if no grade"],
      ["Total Mark", "No", "Number", "Empty if no grade"],
      ["Comment", "No", "Text", "Optional notes"]
    ];
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.aoa_to_sheet(instructions),
      "Instructions"
    );

    XLSX.writeFile(workbook, `${selectedCourse}_${selectedExam}_grades.xlsx`);
  };

  const generatePDFReport = () => {
    if (!selectedExam) return;

    try {
      const doc = new jsPDF();

      // Add logo to the PDF
      const img = new window.Image();
      img.src = logoPath;
      img.onload = () => {
        doc.addImage(img, 'PNG', 14, 10, 30, 20);

        // Title (positioned after logo)
        doc.setFontSize(18);
        doc.setTextColor(40);
        doc.text(`Exam Grades Report - ${selectedCourse}`, 60, 20);

        // Exam Info
        doc.setFontSize(12);
        doc.text(`Exam: ${selectedExam}`, 60, 30);

        // Sort students alphabetically for PDF
        const sortedStudents = filteredStudents.sort((a, b) =>
          (a.userName || '').localeCompare(b.userName || '')
        );

        // Table data with separate Mark and Total Mark columns
        const tableData = sortedStudents.map(student => {
          const grade = student.exam?.grade || "-";
          const gradeColor = grade === "A*" || grade === "A" ? [34, 139, 34] :
            grade === "U" ? [220, 53, 69] :
              grade !== "-" ? [253, 126, 20] :
                [108, 117, 125];

          return [
            student.studentId || "N/A",
            student.userName,
            grade,
            student.exam?.mark || "-",
            student.exam?.comment || "-"
          ];
        });

        // Calculate grade statistics
        const gradeCounts = {};
        gradeOptions.forEach(grade => {
          gradeCounts[grade] = 0;
        });
        gradeCounts["-"] = 0;

        sortedStudents.forEach(student => {
          const grade = student.exam?.grade || "-";
          gradeCounts[grade] = (gradeCounts[grade] || 0) + 1;
        });

        const totalStudents = sortedStudents.length;
        const completedCount = totalStudents - (gradeCounts["-"] || 0);

        // Add table using autoTable
        autoTable(doc, {
          head: [['Student ID', 'Name', 'Grade', 'Mark', 'Comments']],
          body: tableData,
          startY: 45,
          styles: {
            cellPadding: 3,
            fontSize: 10,
            valign: 'middle',
            textColor: [0, 0, 0]
          },
          columnStyles: {
            0: { cellWidth: 25 },
            1: { cellWidth: 'auto' },
            2: { cellWidth: 16 },
            3: { cellWidth: 15 },
            4: { cellWidth: 40 }
          },
          didParseCell: (data) => {
            if (data.column.index === 2) { // Grade column
              const grade = data.cell.raw;
              if (grade === "A*" || grade === "A") {
                data.cell.styles.textColor = [34, 139, 34];
              } else if (grade === "U") {
                data.cell.styles.textColor = [220, 53, 69];
              } else if (grade !== "-") {
                data.cell.styles.textColor = [253, 126, 20];
              }
              data.cell.styles.fontStyle = 'bold';
            }
          }
        });

        // Grade distribution statistics
        const summaryY = doc.lastAutoTable.finalY + 15;

        doc.setFontSize(12);
        doc.text(`Grade Distribution:`, 14, summaryY);

        const gradeSummaryData = [
          ["Grade", "Count", "Percentage"],
          ...gradeOptions.map(grade => [
            grade,
            gradeCounts[grade] || 0,
            completedCount > 0 ? `${Math.round((gradeCounts[grade] / completedCount) * 100)}%` : "0%"
          ]),
          ["Did Not Attend", gradeCounts["-"] || 0, `${Math.round((gradeCounts["-"] / totalStudents) * 100)}%`]
        ];

        autoTable(doc, {
          head: [gradeSummaryData[0]],
          body: gradeSummaryData.slice(1),
          startY: summaryY + 8,
          margin: { left: 14 },
          styles: {
            fontSize: 10,
            cellPadding: 3,
            valign: 'middle'
          },
          columnStyles: {
            0: { cellWidth: 25 },
            1: { cellWidth: 20 },
            2: { cellWidth: 25 }
          },
          didParseCell: (data) => {
            if (data.column.index === 0 && data.row.index < gradeOptions.length) {
              const grade = data.cell.raw;
              if (grade === "A*" || grade === "A") {
                data.cell.styles.textColor = [34, 139, 34];
              } else if (grade === "U") {
                data.cell.styles.textColor = [220, 53, 69];
              } else if (grade === "Grade") {
              } else {
                data.cell.styles.textColor = [253, 126, 20];
              }
              data.cell.styles.fontStyle = 'bold';
            }
          }
        });

        // Footer
        const date = new Date().toLocaleDateString();
        doc.setFontSize(10);
        doc.setTextColor(150);
        doc.text(`Report generated on ${date}`, 14, doc.internal.pageSize.height - 10);

        doc.save(`Exam_Grades_${selectedExam.replace(/\s+/g, '_')}_${date}.pdf`);
      };
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please check console for details.");
    }
  };

  const handleGradeEdit = (student, field, value) => {
    setEditableGrades(prev => ({
      ...prev,
      [student.studentId]: {
        ...prev[student.studentId],
        [selectedExam]: {
          ...prev[student.studentId]?.[selectedExam],
          [field]: value,
          ...(field === 'mark' && { totalMark: examDetails[selectedExam].totalMark })
        }
      }
    }));
  };

  const saveEdits = async () => {
    const updates = {};
    Object.entries(editableGrades).forEach(([studentId, exams]) => {
      Object.entries(exams).forEach(([examName, fields]) => {
        Object.entries(fields).forEach(([field, value]) => {
          updates[`courses/${selectedCourse}/students/${studentId}/examGrades/${examName}/${field}`] = value;
        });
      });
    });

    try {
      await update(ref(db), updates);
      alert("Grades updated successfully!");
      setEditableGrades({});
    } catch (error) {
      console.error("Error updating grades:", error);
      alert("Failed to update grades. Please check the console for details.");
    }
  };

  const createNewExam = async () => {
    const { name, totalMark, date } = newExamDetails;

    if (!name || !totalMark || !date) {
      alert("Please fill in all required fields.");
      return;
    }

    if (!selectedCourse) {
      alert("Please select a course first.");
      return;
    }

    try {
      const examRef = ref(db, `courses/${selectedCourse}/exams/${name}`);
      const examData = {
        name,
        totalMark: parseFloat(totalMark),
        date: date,
        createdAt: new Date().toISOString()
      };

      await set(examRef, examData);
      alert("Exam created successfully!");
      setNewExamDetails({
        name: "",
        totalMark: "",
        date: ""
      });
      setIsCreateExamCollapsed(true);
    } catch (error) {
      console.error("Error creating exam:", error);
      alert("Failed to create exam. Please check the console for details.");
    }
  };

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Exam Grades</h1>

      {/* Course Selection */}
      <div className="bg-white p-4 rounded-lg shadow-lg mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">Select Course</h2>
        <div className="flex flex-wrap gap-2">
          {courses.map(course => (
            <button
              key={course}
              onClick={() => {
                setSelectedCourse(course);
                setSelectedExam(null);
                setActiveSection(null);
              }}
              className={`px-4 py-2 rounded-lg transition-colors ${selectedCourse === course
                ? "bg-teal-600 text-white"
                : "bg-gray-100 hover:bg-gray-200 text-gray-900"
                }`}
            >
              {formatCourseId(course)}
            </button>
          ))}
        </div>
      </div>

      {selectedCourse && (
        <>
          {/* Action Sections */}
          <div className="mb-6">
            {/* Tabs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Create Exam Tab */}
              <button
                onClick={() => setActiveSection(activeSection === 'create' ? null : 'create')}
                className={`p-4 flex justify-between items-center bg-white rounded-lg shadow-lg transition-colors ${activeSection === 'create' ? 'bg-teal-50 border-2 border-teal-500' : 'hover:bg-gray-50'
                  }`}
              >
                <h2 className="text-xl font-semibold text-gray-900">Create New Exam</h2>
                <svg
                  className={`w-6 h-6 transform transition-transform text-gray-600 ${activeSection === 'create' ? 'rotate-180' : ''
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Add Student Grade Tab */}
              <button
                onClick={() => setActiveSection(activeSection === 'add' ? null : 'add')}
                className={`p-4 flex justify-between items-center bg-white rounded-lg shadow-lg transition-colors ${activeSection === 'add' ? 'bg-teal-50 border-2 border-teal-500' : 'hover:bg-gray-50'
                  }`}
              >
                <h2 className="text-xl font-semibold text-gray-900">Add Student Grade</h2>
                <svg
                  className={`w-6 h-6 transform transition-transform text-gray-600 ${activeSection === 'add' ? 'rotate-180' : ''
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Import/Export Tab */}
              <button
                onClick={() => setActiveSection(activeSection === 'import' ? null : 'import')}
                className={`p-4 flex justify-between items-center bg-white rounded-lg shadow-lg transition-colors ${activeSection === 'import' ? 'bg-teal-50 border-2 border-teal-500' : 'hover:bg-gray-50'
                  }`}
              >
                <h2 className="text-xl font-semibold text-gray-900">Import/Export</h2>
                <svg
                  className={`w-6 h-6 transform transition-transform text-gray-600 ${activeSection === 'import' ? 'rotate-180' : ''
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            {/* Create Exam Form */}
            {activeSection === 'create' && (
              <div className="bg-white rounded-lg shadow-lg p-4">
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Exam Name"
                    value={newExamDetails.name}
                    onChange={(e) => setNewExamDetails({ ...newExamDetails, name: e.target.value })}
                    className="w-full border p-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900 placeholder-gray-500"
                    required
                  />
                  <input
                    type="number"
                    placeholder="Total Marks"
                    value={newExamDetails.totalMark}
                    onChange={(e) => setNewExamDetails({ ...newExamDetails, totalMark: e.target.value })}
                    className="w-full border p-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900 placeholder-gray-500"
                    required
                  />
                  <input
                    type="date"
                    value={newExamDetails.date}
                    onChange={(e) => setNewExamDetails({ ...newExamDetails, date: e.target.value })}
                    className="w-full border p-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                    required
                  />
                  <button
                    onClick={createNewExam}
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white p-2 rounded-lg transition-colors"
                  >
                    Create Exam
                  </button>
                </div>
              </div>
            )}

            {/* Add Student Grade Form */}
            {activeSection === 'add' && (
              <div className="bg-white rounded-lg shadow-lg p-4">
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Student ID"
                    value={newExam.studentId}
                    onChange={(e) => setNewExam({ ...newExam, studentId: e.target.value })}
                    className="w-full border p-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900 placeholder-gray-500"
                    required
                  />
                  <select
                    value={newExam.examName}
                    onChange={(e) => setNewExam({ ...newExam, examName: e.target.value })}
                    className="w-full border p-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                    required
                  >
                    <option value="">Select Exam</option>
                    {availableExams.map(exam => (
                      <option key={exam} value={exam}>
                        {exam} ({examDetails[exam]?.totalMark || 0} marks)
                      </option>
                    ))}
                  </select>
                  <div className="grid grid-cols-2 gap-4">
                    <select
                      value={newExam.grade}
                      onChange={(e) => setNewExam({ ...newExam, grade: e.target.value })}
                      className="w-full border p-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                    >
                      <option value="">Select Grade</option>
                      {gradeOptions.map(grade => (
                        <option key={grade} value={grade}>{grade}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      placeholder="Mark"
                      value={newExam.mark}
                      onChange={(e) => setNewExam({ ...newExam, mark: e.target.value })}
                      className="w-full border p-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900 placeholder-gray-500"
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Comment"
                    value={newExam.comment}
                    onChange={(e) => setNewExam({ ...newExam, comment: e.target.value })}
                    className="w-full border p-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900 placeholder-gray-500"
                  />
                  <button
                    onClick={addExam}
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white p-2 rounded-lg transition-colors"
                  >
                    Add Grade
                  </button>
                </div>
              </div>
            )}

            {/* Import/Export Section */}
            {activeSection === 'import' && (
              <div className="bg-white rounded-lg shadow-lg p-4">
                <div className="mb-4 p-4 bg-teal-50 border border-teal-200 rounded-lg">
                  <h3 className="font-bold mb-2 text-teal-900">Excel File Requirements:</h3>
                  <ul className="list-disc pl-5 text-teal-800 space-y-1">
                    <li><strong>Student ID:</strong> Required (must match existing IDs)</li>
                    <li><strong>Grade:</strong> Optional (A*, A, B, etc.) - leave empty if didn't attend</li>
                    <li><strong>Mark:</strong> Optional number</li>
                    <li><strong>Comment:</strong> Optional text</li>
                    <li>Download the template first to see the format</li>
                  </ul>
                </div>

                <div className="space-y-4">
                  <select
                    value={importExamName}
                    onChange={(e) => setImportExamName(e.target.value)}
                    className="w-full border p-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                    required
                  >
                    <option value="">Select Exam</option>
                    {availableExams.map(exam => (
                      <option key={exam} value={exam}>
                        {exam} ({examDetails[exam]?.totalMark || 0} marks)
                      </option>
                    ))}
                  </select>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={handleExcelImport}
                      className="flex-1 border p-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                    />
                    <button
                      onClick={processExcelImport}
                      className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      Import
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleExcelExport}
                      className="flex-1 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      Export to Excel
                    </button>
                    <button
                      onClick={generatePDFReport}
                      className="flex-1 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      Generate PDF Report
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Exam Selection */}
          <div className="bg-white p-4 rounded-lg shadow-lg mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-900">Select Exam</h2>
            <div className="flex flex-wrap gap-2">
              {availableExams.map(examName => (
                <button
                  key={examName}
                  onClick={() => setSelectedExam(examName)}
                  className={`px-4 py-2 rounded-lg transition-colors ${selectedExam === examName
                    ? "bg-teal-600 text-white"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-900"
                    }`}
                >
                  {examName} ({examDetails[examName]?.totalMark || 0} marks)
                  {examDetails[examName]?.date && (
                    <span className="text-sm ml-2">
                      {new Date(examDetails[examName].date).toLocaleDateString()}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Search and Filter */}
          <div className="bg-white p-4 rounded-lg shadow-lg mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Search by Name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border p-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900 placeholder-gray-500"
              />
              <input
                type="text"
                placeholder="Search by Student ID"
                value={searchById}
                onChange={(e) => setSearchById(e.target.value)}
                className="w-full border p-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900 placeholder-gray-500"
              />
            </div>
          </div>

          {/* Grades Table */}
          {selectedExam && (
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Grades for {selectedExam}
                  {examDetails[selectedExam] && (
                    <span className="text-sm text-gray-600 ml-2">
                      Total Marks: {examDetails[selectedExam].totalMark}
                    </span>
                  )}
                </h2>
                <button
                  onClick={saveEdits}
                  className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg transition-colors"
                  disabled={Object.keys(editableGrades).length === 0}
                >
                  Save Changes
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2 text-left text-gray-900">Student ID</th>
                      <th className="border p-2 text-left text-gray-900">Name</th>
                      <th className="border p-2 text-left text-gray-900">Grade</th>
                      <th className="border p-2 text-left text-gray-900">Mark</th>
                      <th className="border p-2 text-left text-gray-900">Comments</th>
                      <th className="border p-2 text-left text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map(student => (
                      <tr key={student.studentId} className="hover:bg-gray-50">
                        <td className="border p-2 text-gray-900">{student.studentId}</td>
                        <td className="border p-2 text-gray-900">{student.userName}</td>
                        <td className="border p-2">
                          <select
                            value={
                              editableGrades[student.studentId]?.[selectedExam]?.grade ??
                              student.exam?.grade ?? ""
                            }
                            onChange={(e) => handleGradeEdit(student, "grade", e.target.value)}
                            className="w-full border p-1 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                          >
                            <option value="">{student.exam ? "No Grade" : "Did Not Attend"}</option>
                            {gradeOptions.map(grade => (
                              <option key={grade} value={grade}>{grade}</option>
                            ))}
                          </select>
                        </td>
                        <td className="border p-2">
                          <input
                            type="number"
                            value={
                              editableGrades[student.studentId]?.[selectedExam]?.mark ??
                              student.exam?.mark ?? ""
                            }
                            onChange={(e) => handleGradeEdit(student, "mark", e.target.value)}
                            className="w-full border p-1 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                          />
                        </td>
                        <td className="border p-2">
                          <input
                            type="text"
                            value={
                              editableGrades[student.studentId]?.[selectedExam]?.comment ??
                              student.exam?.comment ?? ""
                            }
                            onChange={(e) => handleGradeEdit(student, "comment", e.target.value)}
                            className="w-full border p-1 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                          />
                        </td>
                        <td className="border p-2">
                          {student.exam && (
                            <button
                              onClick={() => deleteExam(student.studentId, selectedExam)}
                              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded transition-colors"
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminExamGrades;